import { baseApi } from "./baseApi";

export type ReactionTargetType = "aotd" | "comment";

interface ReactionsResponse {
  success: boolean;
  data: {
    reactions: Record<string, number>;
    userReactions: string[];
    allowedReactions: string[];
  };
}

interface ToggleReactionRequest {
  targetType: ReactionTargetType;
  targetId: string;
  emoji: string;
}

interface ToggleReactionResponse {
  success: boolean;
  action: "added" | "removed";
  emoji: string;
}

export const reactionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getReactions: builder.query<
      ReactionsResponse["data"],
      { targetType: ReactionTargetType; targetId: string }
    >({
      query: ({ targetType, targetId }) =>
        `/reactions/${targetType}/${targetId}`,
      transformResponse: (response: ReactionsResponse) => response.data,
      providesTags: (_result, _error, { targetType, targetId }) => [
        { type: "Reactions" as const, id: `${targetType}-${targetId}` },
      ],
    }),
    toggleReaction: builder.mutation<ToggleReactionResponse, ToggleReactionRequest>({
      query: (body) => ({
        url: "/reactions/toggle",
        method: "POST",
        body,
      }),
      // optimistic update: toggle locally before server responds
      async onQueryStarted({ targetType, targetId, emoji }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          reactionsApi.util.updateQueryData(
            "getReactions",
            { targetType, targetId },
            (draft) => {
              const hasReacted = draft.userReactions.includes(emoji);
              if (hasReacted) {
                draft.userReactions = draft.userReactions.filter((e) => e !== emoji);
                draft.reactions[emoji] = Math.max(0, (draft.reactions[emoji] || 1) - 1);
                if (draft.reactions[emoji] === 0) {
                  delete draft.reactions[emoji];
                }
              } else {
                draft.userReactions.push(emoji);
                draft.reactions[emoji] = (draft.reactions[emoji] || 0) + 1;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetReactionsQuery, useToggleReactionMutation } = reactionsApi;
